import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWorkbook, getQuestions, submitAnswer, generateFinalReview, getFinalReview, getCourseworkStatus } from '../lib/api';
import formatApiError from '../lib/formatApiError';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Circle, 
  Warning,
  PaperPlaneTilt,
  Lightbulb,
  Trophy,
  Spinner,
  Clock,
  CaretLeft,
  CaretRight,
  Lock
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const COMMAND_WORD_INFO = {
  state: { label: 'STATE', description: 'Short, direct, and precise answer', color: 'bg-slate-100 text-slate-800' },
  identify: { label: 'IDENTIFY', description: 'Short, direct identification of key points', color: 'bg-slate-100 text-slate-800' },
  describe: { label: 'DESCRIBE', description: 'Detailed answer showing what something is like', color: 'bg-blue-100 text-blue-800' },
  explain: { label: 'EXPLAIN', description: 'Answer with reasons, meaning, or cause/effect', color: 'bg-purple-100 text-purple-800' },
  outline: { label: 'OUTLINE', description: 'Brief but logically developed answer', color: 'bg-emerald-100 text-emerald-800' },
  list: { label: 'LIST', description: 'Separate concise bullet points', color: 'bg-amber-100 text-amber-800' }
};


const getMinimumBullets = (question) => Math.max(1, question?.min_bullets || 1);

const parseBulletAnswer = (savedAnswer, requiredCount) => {
  const parsed = (savedAnswer || '')
    .split('\n')
    .map((line) => line.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, '').trim())
    .filter(Boolean);

  return Array.from({ length: requiredCount }, (_, index) => parsed[index] || '');
};

const formatBulletAnswer = (bullets) => bullets
  .map((bullet, index) => `${index + 1}. ${bullet.trim()}`)
  .join('\n');

const isAcceptedStatus = (status) => status === 'accepted' || status === 'accepted_with_feedback';

// Staged submit progress (spec §7 PR 7): client-side timed stage messages
// shown while a submission is in flight; if the response arrives early the
// panel completes immediately (it unmounts when submitting ends).
const SUBMIT_STAGES = [
  'Checking your answer…',
  'Comparing against the question requirements…',
  'Writing your feedback…'
];
const SUBMIT_STAGE_INTERVAL_MS = 5000;

// Owner-approved verbatim fallback for non-429 submit errors (PR 7).
const SUBMIT_ERROR_FALLBACK =
  "Something went wrong and your answer wasn't checked. It has not been lost — please try again in a moment.";

// Coursework freeze notice (PR 8a B3): shown on questions locked because the
// learner's coursework is with the assessor. The BACKEND 423 check is the
// enforcement — this render-side lock is presentation. Same owner-editable
// wording as the backend message.
const COURSEWORK_LOCKED_NOTICE =
  'Your coursework has been submitted and is with your assessor, so answers are locked. ' +
  'If anything needs changing, your assessor will return it to you.';

export default function WorkbookView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [workbook, setWorkbook] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [bulletAnswers, setBulletAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitStage, setSubmitStage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [finalReview, setFinalReview] = useState(null);
  const [showFinalReview, setShowFinalReview] = useState(false);
  const [generatingReview, setGeneratingReview] = useState(false);
  const [coursework, setCoursework] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [wbRes, qRes] = await Promise.all([
        getWorkbook(id),
        getQuestions(id)
      ]);
      const loadedQuestions = qRes.data;
      setWorkbook(wbRes.data);
      setQuestions(loadedQuestions);
      
      if (loadedQuestions.length > 0) {
        const firstIncompleteIndex = loadedQuestions.findIndex((question) => !isAcceptedStatus(question.status));
        const startIndex = firstIncompleteIndex === -1 ? loadedQuestions.length - 1 : firstIncompleteIndex;
        const startQuestion = loadedQuestions[startIndex];

        setCurrentIndex(startIndex);
        setAnswer(startQuestion.latest_answer || '');
        setBulletAnswers(
          startQuestion.command_word === 'list'
            ? parseBulletAnswer(startQuestion.latest_answer, getMinimumBullets(startQuestion))
            : []
        );
      } else {
        setCurrentIndex(0);
        setAnswer('');
        setBulletAnswers([]);
      }
      
      // Check if all completed
      const allAccepted = loadedQuestions.length > 0 && loadedQuestions.every((question) => isAcceptedStatus(question.status));
      if (allAccepted) {
        try {
          const reviewRes = await getFinalReview(id);
          setFinalReview(reviewRes.data);
        } catch {
          // No review yet
        }
      }
      // Coursework freeze state (PR 8a): learners only — the backend 423
      // check is the enforcement; this drives the read-only rendering.
      if (user?.role === 'learner') {
        try {
          const courseworkRes = await getCourseworkStatus();
          setCoursework(courseworkRes.data);
        } catch {
          // Freeze rendering is best-effort; the backend still enforces.
        }
      }
    } catch (error) {
      console.error('Failed to load workbook:', error);
    } finally {
      setLoading(false);
    }
  }, [id, user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Update answer when changing questions
    if (questions[currentIndex]) {
      const question = questions[currentIndex];
      setAnswer(question.latest_answer || '');
      setBulletAnswers(
        question.command_word === 'list'
          ? parseBulletAnswer(question.latest_answer, getMinimumBullets(question))
          : []
      );
    }
  }, [currentIndex, questions]);

  useEffect(() => {
    // Error notices belong to the question they arose on.
    setSubmitError('');
  }, [currentIndex]);

  useEffect(() => {
    // Advance the staged progress messages on a client-side timer while a
    // submission is in flight; reset when the response arrives (the panel
    // unmounts, so an early response completes immediately).
    if (!submitting) {
      setSubmitStage(0);
      return undefined;
    }
    const timers = SUBMIT_STAGES.slice(1).map((_, index) =>
      setTimeout(() => setSubmitStage(index + 1), SUBMIT_STAGE_INTERVAL_MS * (index + 1))
    );
    return () => timers.forEach(clearTimeout);
  }, [submitting]);

  const currentQuestion = questions[currentIndex];
  const isListQuestion = currentQuestion?.command_word === 'list';
  const minimumBullets = getMinimumBullets(currentQuestion);
  const writtenWordCount = answer.split(/\s+/).filter(w => w).length;
  const minimumWords = Number(currentQuestion?.min_length) || 0;
  const writtenAnswerComplete = !!answer.trim() && (!minimumWords || writtenWordCount >= minimumWords);
  const listAnswerComplete = isListQuestion && bulletAnswers.length >= minimumBullets && bulletAnswers.slice(0, minimumBullets).every((bullet) => bullet.trim());

  // Coursework freeze (PR 8a B3): submitted/approved locks every question;
  // returned locks everything EXCEPT the reopened questions. The backend 423
  // is the enforcement — this only renders the locked state.
  const courseworkStatusValue = coursework?.status || null;
  const courseworkFrozen = courseworkStatusValue === 'submitted' || courseworkStatusValue === 'approved';
  const reopenedIds = coursework?.reopened_question_ids || [];
  const isReopenedQuestion =
    courseworkStatusValue === 'returned' && !!currentQuestion && reopenedIds.includes(currentQuestion._id);
  const questionLockedByCoursework = !!currentQuestion && (
    courseworkFrozen ||
    (courseworkStatusValue === 'returned' && !isReopenedQuestion && !isAcceptedStatus(currentQuestion.status))
  );

  const submitDisabled = submitting || questionLockedByCoursework || (isListQuestion ? !listAnswerComplete : !writtenAnswerComplete);
  const acceptedCount = questions.filter(q => q.status === 'accepted' || q.status === 'accepted_with_feedback').length;
  const allAccepted = acceptedCount === questions.length && questions.length > 0;
  const progress = questions.length > 0 ? Math.round((acceptedCount / questions.length) * 100) : 0;

  const handleSubmit = async () => {
    if (!currentQuestion) return;

    const isListQuestion = currentQuestion.command_word === 'list';
    const answerToSubmit = isListQuestion ? formatBulletAnswer(bulletAnswers) : answer;
    if (!answerToSubmit.trim()) return;
    if (!isListQuestion) {
      const minWords = Number(currentQuestion.min_length) || 0;
      if (minWords && answerToSubmit.split(/\s+/).filter(w => w).length < minWords) return;
    }
    
    setSubmitError('');
    setSubmitting(true);
    try {
      const response = await submitAnswer(currentQuestion._id, answerToSubmit);
      
      // Update local state
      const updatedQuestions = [...questions];
      updatedQuestions[currentIndex] = {
        ...updatedQuestions[currentIndex],
        status: response.data.status,
        latest_answer: answerToSubmit,
        latest_feedback: response.data.ai_feedback,
        latest_bullet_results: response.data.bullet_results || [],
        attempt_count: response.data.attempt_number
      };
      setQuestions(updatedQuestions);
      
      // If accepted (including accepted_with_feedback), move to next question
      const isAccepted = response.data.status === 'accepted' || response.data.status === 'accepted_with_feedback';
      if (isAccepted && currentIndex < questions.length - 1) {
        setTimeout(() => {
          setCurrentIndex(currentIndex + 1);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      // PR 7 submit error handling: the learner's typed answer is never
      // cleared by an error path — only the notice below changes. A 429
      // carries the backend's rate-limit message (with its wait estimate);
      // anything else shows the owner-approved generic sentence.
      // A 423 (PR 8a) carries the coursework-frozen message from the backend.
      if (error?.response?.status === 429 || error?.response?.status === 423) {
        setSubmitError(formatApiError(error));
      } else {
        setSubmitError(SUBMIT_ERROR_FALLBACK);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateFinalReview = async () => {
    setGeneratingReview(true);
    try {
      const response = await generateFinalReview(id);
      setFinalReview(response.data);
      setShowFinalReview(true);
    } catch (error) {
      console.error('Failed to generate review:', error);
    } finally {
      setGeneratingReview(false);
    }
  };

  const canMoveNext = currentQuestion?.status === 'accepted' || currentQuestion?.status === 'accepted_with_feedback' || currentIndex < questions.findIndex(q => q.status !== 'accepted' && q.status !== 'accepted_with_feedback');
  const canMovePrev = currentIndex > 0;

  const goToQuestion = (index) => {
    // Can only go to questions that are before the first non-accepted question, or are accepted
    const firstNonAcceptedIndex = questions.findIndex(q => q.status !== 'accepted' && q.status !== 'accepted_with_feedback');
    if (firstNonAcceptedIndex === -1 || index <= firstNonAcceptedIndex) {
      setCurrentIndex(index);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle size={16} weight="fill" className="text-emerald-500" />;
      case 'accepted_with_feedback':
        return <CheckCircle size={16} weight="fill" className="text-blue-500" />;
      case 'needs_improvement':
        return <Warning size={16} weight="fill" className="text-amber-500" />;
      case 'draft':
        return <Clock size={16} weight="fill" className="text-blue-500" />;
      default:
        return <Circle size={16} className="text-slate-300" />;
    }
  };

  const listBulletResults = isListQuestion ? (currentQuestion?.latest_bullet_results || []) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-[#3B5A7A]" />
      </div>
    );
  }

  if (showFinalReview && finalReview) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="final-review-view">
        <header className="app-header">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <button onClick={() => setShowFinalReview(false)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={20} />
              <span>Back to Workbook</span>
            </button>
          </div>
        </header>
        
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="card p-8 animate-fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Trophy size={32} weight="duotone" className="text-[#3B5A7A]" />
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Final Assessment Review</h1>
            </div>
            
            {finalReview.review_data && (
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-[#3B5A7A] text-white">
                  <p className="text-sm uppercase tracking-wider opacity-80 mb-1">Overall Grade</p>
                  <p className="text-2xl font-bold">{finalReview.review_data.overall_grade || 'N/A'}</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Summary</h3>
                  <p className="text-slate-600">{finalReview.review_data.summary}</p>
                </div>
                
                {finalReview.review_data.strengths?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-emerald-700 mb-2">Strengths</h3>
                    <ul className="space-y-2">
                      {finalReview.review_data.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <CheckCircle size={18} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {finalReview.review_data.weaknesses?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-amber-700 mb-2">Areas for Improvement</h3>
                    <ul className="space-y-2">
                      {finalReview.review_data.weaknesses.map((w, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <Warning size={18} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {finalReview.review_data.outstanding_answers?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-emerald-700 mb-2">Outstanding Answers</h3>
                    <ul className="space-y-2">
                      {finalReview.review_data.outstanding_answers.map((item, i) => (
                        <li key={i} className="text-slate-600">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {finalReview.review_data.accepted_but_improvable?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-blue-700 mb-2">Accepted but Could Be Stronger</h3>
                    <ul className="space-y-2">
                      {finalReview.review_data.accepted_but_improvable.map((item, i) => (
                        <li key={i} className="text-slate-600">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {finalReview.review_data.improvement_themes?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-amber-700 mb-2">Overall Improvement Themes</h3>
                    <ul className="space-y-2">
                      {finalReview.review_data.improvement_themes.map((theme, i) => (
                        <li key={i} className="text-slate-600">• {theme}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {finalReview.review_data.fail_risks?.length > 0 && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <h3 className="font-semibold text-red-700 mb-2">Fail Risk Flags</h3>
                    <ul className="space-y-1 text-red-600">
                      {finalReview.review_data.fail_risks.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="workbook-view">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
            <div className="flex-1 text-center">
              <h1 className="text-lg font-semibold text-slate-900 font-['Manrope'] truncate px-4">
                {workbook?.title}
              </h1>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Progress value={progress} className="h-2" />
            </div>
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
              {acceptedCount} / {questions.length} completed
            </span>
          </div>
        </div>
      </div>

      {/* Question Navigation Pills */}
      <div className="bg-white border-b border-slate-200 py-3 overflow-x-auto">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-2">
            {questions.map((q, index) => {
              const firstNonAcceptedIndex = questions.findIndex(ques => !isAcceptedStatus(ques.status));
              const isAccessible = firstNonAcceptedIndex === -1 || index <= firstNonAcceptedIndex;
              
              const isReopenedPill = courseworkStatusValue === 'returned' && reopenedIds.includes(q._id);
              return (
                <button
                  key={q._id}
                  onClick={() => isAccessible && goToQuestion(index)}
                  disabled={!isAccessible}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    index === currentIndex
                      ? 'bg-[#3B5A7A] text-white'
                      : isAccessible
                        ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                  }${isReopenedPill ? ' ring-2 ring-amber-400' : ''}`}
                  data-testid={`question-nav-${index}`}
                >
                  {getStatusIcon(q.status)}
                  <span>Q{index + 1}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {currentQuestion && (
            <motion.div
              key={currentQuestion._id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="card p-8 sm:p-12"
            >
              {/* Question Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm text-slate-400 font-medium">
                    Question {currentIndex + 1} of {questions.length}
                  </span>
                  {currentQuestion.spec_ref && (
                    <span className="text-xs mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                      Ref: {currentQuestion.spec_ref}
                    </span>
                  )}
                  {isReopenedQuestion && (
                    <span
                      className="text-xs font-medium text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full"
                      data-testid="reopened-question-chip"
                    >
                      Returned by your assessor — action needed
                    </span>
                  )}
                </div>
                
                {/* Command Word Badge */}
                {currentQuestion.command_word && (
                  <div className="mb-4">
                    <span className={`command-word-badge ${COMMAND_WORD_INFO[currentQuestion.command_word]?.color || ''}`}>
                      {COMMAND_WORD_INFO[currentQuestion.command_word]?.label || currentQuestion.command_word.toUpperCase()}
                    </span>
                    <p className="text-xs text-slate-500 mt-1">
                      {COMMAND_WORD_INFO[currentQuestion.command_word]?.description}
                    </p>
                  </div>
                )}

                {/* Question Text */}
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 font-['Manrope'] leading-snug">
                  {currentQuestion.text}
                </h2>

                {/* Guidance */}
                {currentQuestion.guidance && (
                  <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="flex items-start gap-2">
                      <Lightbulb size={20} weight="duotone" className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 mb-1">Guidance</p>
                        <p className="text-sm text-blue-700">{currentQuestion.guidance}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Answer Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Your Answer
                  </label>
                  {isListQuestion ? (
                    <div className="space-y-3">
                      {Array.from({ length: minimumBullets }, (_, index) => {
                        const bulletResult = listBulletResults.find((item) => item.index === index + 1);
                        const isAcceptedBullet = bulletResult?.status === 'accepted';
                        // Owner-approved display fix (PR 7): when the question
                        // is in an accepted-family state (e.g. after an
                        // override-to-accept), stale per-bullet warning icons
                        // and amber styling are not rendered; genuine green
                        // accepted ticks remain. Stored bullet_results are
                        // untouched — marking evidence preserved.
                        const isNeedsImprovementBullet =
                          bulletResult?.status === 'needs_improvement' && !isAcceptedStatus(currentQuestion.status);
                        const bulletStateClass = isAcceptedBullet
                          ? 'bg-emerald-50 border-emerald-400 focus:ring-emerald-500'
                          : isNeedsImprovementBullet
                            ? 'bg-amber-50 border-amber-400 focus:ring-amber-500'
                            : 'bg-slate-50 border-slate-200 focus:ring-[#3B5A7A]';
                        return (
                        <div key={index}>
                          <label className="block text-xs font-medium text-slate-500 mb-1">
                            Bullet {index + 1} <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-500 w-6">{index + 1}.</span>
                            <Input
                              value={bulletAnswers[index] || ''}
                              onChange={(e) => {
                                const updated = [...bulletAnswers];
                                updated[index] = e.target.value;
                                setBulletAnswers(updated);
                                setSubmitError('');
                              }}
                              placeholder={`Required bullet point ${index + 1}`}
                              className={`border rounded-md focus:bg-white focus:ring-2 focus:border-transparent transition-all ${bulletStateClass}`}
                              disabled={isAcceptedBullet || isAcceptedStatus(currentQuestion.status) || questionLockedByCoursework}
                              data-testid={`bullet-answer-input-${index + 1}`}
                            />
                            {isAcceptedBullet && (
                              <CheckCircle size={18} weight="fill" className="text-emerald-600 flex-shrink-0" />
                            )}
                            {isNeedsImprovementBullet && (
                              <Warning size={18} weight="fill" className="text-amber-500 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      )})}
                      <p className="text-xs text-slate-400">Minimum: {minimumBullets} required bullet points</p>
                    </div>
                  ) : (
                    <>
                      <Textarea
                        value={answer}
                        onChange={(e) => { setAnswer(e.target.value); setSubmitError(''); }}
                        placeholder="Type your answer here..."
                        className="min-h-[200px] p-4 bg-slate-50 border border-slate-200 rounded-md focus:bg-white focus:ring-2 focus:ring-[#3B5A7A] focus:border-transparent transition-all resize-y"
                        disabled={isAcceptedStatus(currentQuestion.status) || questionLockedByCoursework}
                        data-testid="answer-textarea"
                      />
                      <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                        <span>{writtenWordCount} words</span>
                        {currentQuestion.min_length && (
                          <span>Minimum: {currentQuestion.min_length} words required</span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* AI Feedback */}
                {isAcceptedStatus(currentQuestion.status) && (
                  <div className="ai-feedback">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={24} weight="duotone" className="text-[#3B5A7A] flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1 text-[#3B5A7A]">Answer Accepted</p>
                        {/* Owner ruling 3 (10 July 2026): the question page shows
                            feedback text ONLY for needs_improvement. Accepted and
                            accepted_with_feedback show this banner exactly as-is;
                            their stored feedback surfaces at final review (PR 8). */}
                        <p className="text-slate-600 text-sm">
                          Your answer meets the required standard.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {currentQuestion.status === 'needs_improvement' && currentQuestion.latest_feedback && (
                  <div className="ai-feedback-improvement">
                    <div className="flex items-start gap-3">
                      <Warning size={24} weight="duotone" className="text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="font-semibold mb-1 text-amber-700">Improvement Needed</p>
                        <p className="text-slate-600 text-sm">{currentQuestion.latest_feedback}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Staged submit progress (spec §7 PR 7) — client-side timed
                    stages with an animated bar while the submission is in
                    flight; unmounts (completes) as soon as the response
                    arrives. */}
                {submitting && (
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="submit-progress-panel">
                    <p className="text-sm font-medium text-slate-700" data-testid="submit-progress-stage">
                      {SUBMIT_STAGES[submitStage]}
                    </p>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-[#3B5A7A] submit-progress-bar" />
                    </div>
                    <p className="mt-2 text-xs text-slate-400">usually takes about 15 seconds</p>
                  </div>
                )}

                {/* Submit error notice (PR 7): 429 rate-limit message or the
                    generic failure sentence; clears on edit or resubmit. The
                    typed answer is never cleared by an error path. */}
                {submitError && !submitting && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="submit-error-notice">
                    <div className="flex items-start gap-2">
                      <Warning size={18} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">{submitError}</p>
                    </div>
                  </div>
                )}

                {/* Coursework locked notice (PR 8a): frozen questions render
                    read-only; the backend 423 check is the enforcement. */}
                {questionLockedByCoursework && (
                  <div className="p-4 rounded-lg bg-slate-100 border border-slate-200" data-testid="coursework-locked-notice">
                    <div className="flex items-start gap-2">
                      <Lock size={18} weight="duotone" className="text-slate-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-600">{COURSEWORK_LOCKED_NOTICE}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                {!isAcceptedStatus(currentQuestion.status) && !questionLockedByCoursework && (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    className="w-full h-12 bg-[#3B5A7A] hover:bg-[#2A4158] text-white font-medium"
                    data-testid="submit-answer-btn"
                  >
                    {submitting ? (
                      <>
                        <Spinner size={20} className="animate-spin mr-2" />
                        Evaluating...
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={20} className="mr-2" />
                        Submit Answer
                      </>
                    )}
                  </Button>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentIndex(currentIndex - 1)}
                    disabled={!canMovePrev}
                    className="text-slate-600"
                    data-testid="prev-question-btn"
                  >
                    <CaretLeft size={20} className="mr-1" />
                    Previous
                  </Button>
                  
                  {currentIndex === questions.length - 1 && allAccepted ? (
                    <Button
                      onClick={handleGenerateFinalReview}
                      disabled={generatingReview}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="generate-review-btn"
                    >
                      {generatingReview ? (
                        <>
                          <Spinner size={20} className="animate-spin mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Trophy size={20} className="mr-2" />
                          View Final Review
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => setCurrentIndex(currentIndex + 1)}
                      disabled={!canMoveNext || currentIndex >= questions.length - 1}
                      className="text-slate-600"
                      data-testid="next-question-btn"
                    >
                      Next
                      <CaretRight size={20} className="ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {questions.length === 0 && (
          <div className="card p-12 text-center">
            <Circle size={48} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Questions Yet</h3>
            <p className="text-slate-500">This workbook doesn't have any questions yet.</p>
          </div>
        )}
      </main>
    </div>
  );
}
